import { USER_ROLE } from '@modules/identity/api/const';
import {
  BUSINESS_IDENTIFIER, TRANSACTION_ACTION_TYPES,
  CREDITS_CHARGE_ACTION_TYPES
} from './const';

export type BrokerProfileResponse = {
  external_id: string;
  device_token: string;
  roles: UserRolesType[];
  is_granted_access_to_mb_portal: boolean;
  user_type: string;
  brokerage_external_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  terms_conditions_version: string;
};

export type UserRolesType = typeof USER_ROLE[keyof typeof USER_ROLE];

export type BusinessIdentifier = typeof BUSINESS_IDENTIFIER[keyof typeof BUSINESS_IDENTIFIER];

export type DirectFeatureFlagResponse = {
  [flagKey: string]: {
    key: string;
  }
};

export const ANNOUNCEMENTS_FEATURE_FLAGS = {
  REFER_AND_EARN_ANNOUNCEMENT:
    'client-hub-announcement-refer-and-earn',
} as const;

export const AMPLITUDE_FEATURE_FLAGS = {
  CLIENT_HUB_MAINTENANCE_MODE: 'client-hub-maintenance-mode',
  CLIENT_HUB_CREDITS_MODULE: 'client-hub-credits-module',
  CLIENT_HUB_CREATE_CASE: 'client-hub-create-case',
  CLIENT_HUB_CASE_SUBMISSION_DIRECT_TO_BANK: 'client-hub-case-submission-direct-to-bank',
  CLIENT_HUB_BANK_PRODUCTS_SHOW_FEE_FINANCING_COLUMN: 'client-hub-bank-products-show-fee-financing-column',
  CLIENT_HUB_NEW_PCS_CREDITS_FLOW: 'client-hub-new-pcs-credits-flow',
  CLIENT_HUB_PAYMENT_GATEWAY: 'client-hub-payment-gateway',
  CLIENT_HUB_LOW_CREDITS_FEATURE_BLOCKING: 'client-hub-low-credits-feature-blocking',
  CLIENT_HUB_CREDIT_EXPIRATION_BANNER: 'client-hub-credit-expiration-banner',
  CLIENT_HUB_ENABLE_REFER_AND_EARN: 'client-hub-enable-refer-and-earn',
  CLIENT_VAULT_AUTO_SAVE: 'client-vault-auto-save',
  CLIENT_HUB_ESIGNATURE_FLOW: 'client-hub-esignature-flow',
  CLIENT_HUB_PROPOSALS_MODULE: 'client-hub-proposals-module',
  CLIENT_HUB_DASHBOARDS_MODULE: 'client-hub-dashboards-module',
  CLIENT_HUB_BANKS_MODULE: 'client-hub-banks-module',
  CLIENT_HUB_PAYOUTS_MODULE: 'client-hub-payouts-module',
  CLIENT_HUB_TUTORIALS_MODULE: 'client-hub-tutorials-module',
  CLIENT_HUB_PROPOSALS_BAYTECA_MVP: 'client-hub-proposals-bayteca-mvp',
  CLIENT_HUB_COMPANY_CLIENTS: 'client-hub-company-clients',
  CLIENT_HUB_MORTGAGE_USAGE: 'client-hub-mortgage-usage',
  CLIENT_HUB_BROKER_PHONE_NUMBER_EDITABLE: 'client-hub-broker-phone-number-editable',
  CLIENT_HUB_REFER_AND_EARN_LIST_TABLE: 'client-hub-refer-and-earn-list-table',
  ...ANNOUNCEMENTS_FEATURE_FLAGS,
} as const;

export const DEFAULT_ACTIVE_FEATURE_FLAGS = [
  AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_LOW_CREDITS_FEATURE_BLOCKING,
  AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_NEW_PCS_CREDITS_FLOW,
  AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_CREDITS_MODULE,
  AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_PAYMENT_GATEWAY,
  AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_COMPANY_CLIENTS,
  AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_TUTORIALS_MODULE
];

export type FeatureFlagsResponse = {
  [K in keyof typeof AMPLITUDE_FEATURE_FLAGS as typeof AMPLITUDE_FEATURE_FLAGS[K]]: {
    key: string;
  }
};

export type FeatureFlags = {
  [K in keyof typeof AMPLITUDE_FEATURE_FLAGS as typeof AMPLITUDE_FEATURE_FLAGS[K]]: boolean
};

export type Path<T, S, K extends keyof T = keyof T> = K extends string
  ? T[K] extends S // Stop when we encounter
    ? `${K}`
    : T[K] extends Record<string, unknown>
      ? `${K}` | `${K}.${Path<T[K], S>}`
      : `${K}`
  : never;

export type FeatureSetting = {
  enabled?: boolean;
  dependsOn?: Path<FeatureConfig, FeatureSetting>[];
};

export type FeatureConfig = {
  [moduleName: string]: {
    [featureName: string]: FeatureSetting
  }
};

export type BrokerageAccount = {
  name?: string;
  primary_color?: string;
  logo_url?: string;
  created_at?: string;
  updated_at?: string;
};

export type CreditsResponse = {
  available_credits: number;
};

export type CreditsTransaction = {
  transaction_id: string;
  action_type: typeof TRANSACTION_ACTION_TYPES[keyof typeof TRANSACTION_ACTION_TYPES];
  amount: string;
  owner_id: string;
  account_id: string;
  timestamp: string;
};

export type CreditsTransactionsResponse = {
  transactions: CreditsTransaction[];
  limit: number;
  page: number;
  total: number;
};

export type CreditsPriceListActionName = typeof CREDITS_CHARGE_ACTION_TYPES[keyof typeof CREDITS_CHARGE_ACTION_TYPES];

export type CreditPrice = {
  action_type: CreditsPriceListActionName;
  amount: number;
};

export type CreditsPriceListResponse = {
  action_type_configurations: CreditPrice[]
};

export type UpdateBrokerAccount = {
  phone_number: string;
};
